import React, {useCallback, useEffect, useRef, useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {debounce} from "lodash";
import {Form} from "../../form/Form/Form";
import '../entity-form.scss';
import {useToastContext} from "../../../context/toastContext";
import {BaseEntityNameLess} from "../../../api/entities/BaseEntity";
import {MetadataPropertyEntity} from "../../../api/entities/replancity_MetadataProperty";
import FieldCreator from "../../form/FieldCreator";
import {useTheme} from "../../../context/themeContext";
import {correctFormDataObjBeforeRequest, getFormDataObj} from "../../../utils/utils";
import {FormDataObj} from "../../../utils/types";
import {DeleteIcon} from "../../icons/icons/DeleteIcon";
import {IconButton} from "../../buttons/IconButton/IconButton";
import DeleteConfirmationModal from "../DeleteEntityModal/DeleteConfirmationModal";
import {EntityMetadata, getVisibleFieldsByFormChange} from "../../../hooks/entites/useEntityLoader";
import Tooltip from "@mui/material/Tooltip";


export type EntityFormMetadataProperty = Omit<MetadataPropertyEntity, 'showIf' | 'mandatoryIf'> & {
    // eslint-disable-next-line @typescript-eslint/ban-types
    showIf: Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    mandatoryIf: Function;
};

export type EntityFormProps = {
    loading: boolean;
    entity: BaseEntityNameLess; //remove?
    metadata: EntityMetadata | undefined;
    changeFormFn?: (formDataObj: FormDataObj) => void;
    saveFn: <T, >(formDataObj: FormDataObj, reloadRequired?: boolean) => Promise<void>;
    deleteFn?: ({entity}: { entity: BaseEntityNameLess }) => Promise<void>;
    showDeleteBtn?: boolean;
    deleteTooltip?: string;
    deleteModalTitle?: string;
    icons?: JSX.Element | JSX.Element[];
}

const EntityForm = ({
                        loading,
                        entity,
                        metadata,
                        changeFormFn,
                        saveFn,
                        deleteFn,
                        showDeleteBtn = true,
                        deleteTooltip,
                        deleteModalTitle,
                        icons,
                        ...props
                    }: EntityFormProps) => {
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const {addToast} = useToastContext();
    const {t} = useTranslation();
    const {theme} = useTheme();

    useEffect(() => {
        setFormDisabled(!!formRef.current && !formRef.current.checkValidity());
    });

    const onFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        const {currentTarget} = event;

        const formDataEntryObj = getFormDataObj(event);
        const {visibleMetadataByName} = metadata ?? {};
        const formDataObj = correctFormDataObjBeforeRequest(formDataEntryObj, visibleMetadataByName ?? {});

        if (!currentTarget.reportValidity()) {
            addToast(`${t('common.not-all-required-fields-are-filled')}`, 'error');
            return;
        }

        setFormDisabled(true);
        await saveFn(formDataObj);
        setFormDisabled(false);
    }, [metadata, saveFn, addToast, t]);

    const formChangeHandler = debounce(useCallback(async (event) => {
        const {target, nativeEvent} = event;

        // event.currentTarget can't be used, because it disappeared in debounce
        event.currentTarget = formRef.current;

        setFormDisabled(!formRef.current?.checkValidity());

        const formDataObj = getFormDataObj(event);

        const {visibleMetadataByName} = metadata ?? {};

        const obj = getVisibleFieldsByFormChange({
            fieldName: nativeEvent.target.name,
            visibleMetadataByName: visibleMetadataByName ?? {},
            formDataObj
        })

        changeFormFn?.(obj as FormDataObj);
    }, [changeFormFn, metadata]), 300);

    const getFormFields = (metaData: EntityMetadata, entity: any) => {
        const {visibleMetadataByName, orderedNames} = metaData;

        return orderedNames.map((name, ind) => {
                if (visibleMetadataByName[name]) {
                    const {mandatoryIf} = visibleMetadataByName[name];
                    const required = mandatoryIf.apply(entity);

                    return (
                        <React.Fragment key={name}>
                            {
                                Object.hasOwn(entity, name)
                                    ? <FieldCreator
                                        value={entity[name]}
                                        required={required}
                                        metadata={visibleMetadataByName[name]}
                                        autoFocus={ind === 0}
                                        toShowField={Object.hasOwn(entity, name)}
                                        // ref={el => (fieldsRefs.current[name] = el)}
                                    />
                                    : null
                            }
                        </React.Fragment>
                    )
                }
            }
        )
    }

    const deleteEntityHandler = useCallback(async (): Promise<void> => {
        if (entity) {
            await deleteFn?.({entity});
        }
    }, [entity, deleteFn])

    return (
        <div className={`entity-editor ${theme}`} data-testid="entityEditor">
            <div className="entity-editor__header">
                <div className="title">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {loading
                            ? null
                            : metadata ? entity?.name : t('map.no-editable-fields')}
                    </Typography>
                </div>
                <div className="actions">
                    {
                        icons
                            ? icons
                            : null
                    }
                    {
                        showDeleteBtn && deleteFn ?
                            <Tooltip title={deleteTooltip} placement="top">
                                <IconButton
                                    icon={DeleteIcon}
                                    width={20}
                                    height={20}
                                    onClick={() => setShowDeleteConfirmation(true)}
                                    borderless
                                    testId="entityFormDeleteBtn"
                                />
                            </Tooltip>
                            : null
                    }
                    {/*{props.summaryIcon?.(entity?.id)}*/}
                </div>
            </div>
            {
                metadata
                    ? <Form
                        name="entityForm"
                        onSubmit={onFormSubmit}
                        onChange={formChangeHandler}
                        submitBtnTitle={t('common.save')}
                        disabled={formDisabled}
                        ref={formRef}
                        submitBtnTestId="entityFormSubmitBtn"
                        testId="entityForm"
                    >
                        {getFormFields(metadata, entity)}
                    </Form>
                    : null
            }
            {
                showDeleteConfirmation
                    ? <DeleteConfirmationModal
                        setModalShow={setShowDeleteConfirmation}
                        deleteFn={deleteEntityHandler}
                        title={deleteModalTitle}
                    />
                    : null
            }
        </div>
    )
}

export default React.memo(EntityForm);